import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { LegalLinks } from '../components/LegalLinks';
import { BookOpenIcon, AwardIcon, MonitorIcon, ClipboardCheckIcon, BriefcaseIcon, LaptopIcon, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, LoaderIcon, GraduationCapIcon, UsersIcon, ClockIcon, DollarSignIcon, ShieldCheckIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
const uagcEducationLevels = [{
  label: 'High School Diploma',
  value: '1'
}, {
  label: 'No High School Diploma',
  value: '2'
}, {
  label: 'GED',
  value: '3'
}, {
  label: '0-12 College Credits',
  value: '4'
}, {
  label: 'More Than 12 College Credits',
  value: '5'
}, {
  label: 'Associates Degree',
  value: '6'
}, {
  label: 'Bachelors Degree',
  value: '7'
}];
const uagcStartTimeframes = [{
  label: 'Immediately',
  value: '1'
}, {
  label: '1-3 Months',
  value: '2'
}, {
  label: '4-6 Months',
  value: '3'
}, {
  label: '7-12 Months',
  value: '4'
}, {
  label: 'Later Than 1 Year',
  value: '5'
}];
function getGradYearOptions(): string[] {
  const years: string[] = [];
  for (let y = 2026; y >= 1980; y--) {
    years.push(String(y));
  }
  return years;
}
const gradYearOptions = getGradYearOptions();
const degreeLevels = [{
  label: 'Associate',
  value: 'associate'
}, {
  label: "Bachelor's",
  value: 'bachelors'
}];
interface UagcProgram {
  shortTitle: string;
  longTitle: string;
  degreeLevel: string;
  areaOfStudy: string;
  mProgram: string;
}
const uagcPrograms: UagcProgram[] = [
// Associate - Business
{
  shortTitle: 'A.A. - Business',
  longTitle: 'Associate of Arts (A.A.) in Business',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54516'
}, {
  shortTitle: 'A.A. - Organizational Management',
  longTitle: 'Associate of Arts (A.A.) in Organizational Management',
  degreeLevel: 'associate',
  areaOfStudy: 'business',
  mProgram: '54517'
},
// Associate - Education
{
  shortTitle: 'A.A. - Early Childhood Education',
  longTitle: 'Associate of Arts (A.A.) in Early Childhood Education',
  degreeLevel: 'associate',
  areaOfStudy: 'education',
  mProgram: '1639'
},
// Associate - Liberal Arts
{
  shortTitle: 'A.A. - Military Studies',
  longTitle: 'Associate of Arts (A.A.) in Military Studies',
  degreeLevel: 'associate',
  areaOfStudy: 'liberal-arts',
  mProgram: '54693'
},
// Bachelor's - Business
{
  shortTitle: 'B.A. - Accounting',
  longTitle: 'Bachelor of Arts (B.A.) in Accounting',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1610'
}, {
  shortTitle: 'B.A. - Business Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Business Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1608'
}, {
  shortTitle: 'B.A. - Business Leadership',
  longTitle: 'Bachelor of Arts (B.A.) in Business Leadership',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1611'
}, {
  shortTitle: 'B.A. - Finance',
  longTitle: 'Bachelor of Arts (B.A.) in Finance',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1614'
}, {
  shortTitle: 'B.A. - Human Resources Management',
  longTitle: 'Bachelor of Arts (B.A.) in Human Resources Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1618'
}, {
  shortTitle: 'B.A. - Marketing',
  longTitle: 'Bachelor of Arts (B.A.) in Marketing',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1619'
}, {
  shortTitle: 'B.A. - Organizational Management',
  longTitle: 'Bachelor of Arts (B.A.) in Organizational Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1621'
}, {
  shortTitle: 'B.A. - Project Management',
  longTitle: 'Bachelor of Arts (B.A.) in Project Management',
  degreeLevel: 'bachelors',
  areaOfStudy: 'business',
  mProgram: '1622'
},
// Bachelor's - Criminal Justice
{
  shortTitle: 'B.A. - Social and Criminal Justice',
  longTitle: 'Bachelor of Arts (B.A.) in Social and Criminal Justice',
  degreeLevel: 'bachelors',
  areaOfStudy: 'criminal-justice',
  mProgram: '1642'
},
// Bachelor's - Education
{
  shortTitle: 'B.A. - Early Childhood Development w/ Differentiated Instruction',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Development w/ Differentiated Instruction',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1645'
}, {
  shortTitle: 'B.A. - Early Childhood Education',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1612'
}, {
  shortTitle: 'B.A. - Early Childhood Education Administration',
  longTitle: 'Bachelor of Arts (B.A.) in Early Childhood Education Administration',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1613'
}, {
  shortTitle: 'B.A. - Education Studies',
  longTitle: 'Bachelor of Arts (B.A.) in Education Studies',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1637'
}, {
  shortTitle: 'B.A. - Instructional Design',
  longTitle: 'Bachelor of Arts (B.A.) in Instructional Design',
  degreeLevel: 'bachelors',
  areaOfStudy: 'education',
  mProgram: '1641'
},
// Bachelor's - Information Technology
{
  shortTitle: 'B.A. - Business Information Systems',
  longTitle: 'Bachelor of Arts (B.A.) in Business Information Systems',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1609'
}, {
  shortTitle: 'B.A. - Information Technology',
  longTitle: 'Bachelor of Arts (B.A.) in Information Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1625'
}, {
  shortTitle: 'B.S. - Computer Software Technology',
  longTitle: 'Bachelor of Science (B.S.) in Computer Software Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1628'
}, {
  shortTitle: 'B.S. - Cyber & Data Security Technology',
  longTitle: 'Bachelor of Science (B.S.) in Cyber & Data Security Technology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'information-technology',
  mProgram: '1640'
},
// Bachelor's - Social & Behavioral Science
{
  shortTitle: 'B.A. - Applied Behavioral Science',
  longTitle: 'Bachelor of Arts (B.A.) in Applied Behavioral Science',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1643'
}, {
  shortTitle: 'B.A. - Psychology',
  longTitle: 'Bachelor of Arts (B.A.) in Psychology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1671'
}, {
  shortTitle: 'B.A. - Sociology',
  longTitle: 'Bachelor of Arts (B.A.) in Sociology',
  degreeLevel: 'bachelors',
  areaOfStudy: 'social-behavioral-science',
  mProgram: '1672'
}];
export function UAGCLandingPage() {
  usePageMeta('University of Arizona Global Campus | Online Degree Programs', 'Explore flexible online degree programs at The University of Arizona Global Campus. Accredited programs in Business, Criminal Justice, and Education designed for working adults.', {
    ogImage: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
    ogType: 'website'
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLDivElement>(null);
  const [selectedDegreeLevel, setSelectedDegreeLevel] = useState('');
  const [selectedAreaOfStudy, setSelectedAreaOfStudy] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [formStep, setFormStep] = useState(0);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [urlParams] = useState<Record<string, string>>(() => {
    const params: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  });
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => setUserIP(data.ip || '')).catch(() => setUserIP(''));
  }, []);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
          setZipLookedUp(true);
        }
      } else {
        setZipLookedUp(true);
      }
    } catch {
      setZipLookedUp(true);
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const areaLabels: Record<string, string> = {
    business: 'Business',
    'criminal-justice': 'Criminal Justice',
    education: 'Education',
    'information-technology': 'Information Technology',
    'liberal-arts': 'Liberal Arts',
    'social-behavioral-science': 'Social & Behavioral Science'
  };
  const allAreaSlugs = Object.keys(areaLabels);
  // Get available areas based on selected degree level
  const getAvailableAreas = () => {
    if (!selectedDegreeLevel || selectedDegreeLevel === 'undecided') {
      return allAreaSlugs;
    }
    const areasWithPrograms = new Set<string>();
    uagcPrograms.forEach(prog => {
      if (prog.degreeLevel === selectedDegreeLevel) {
        areasWithPrograms.add(prog.areaOfStudy);
      }
    });
    return allAreaSlugs.filter(slug => areasWithPrograms.has(slug));
  };
  // Get available programs based on selected area and degree level
  const getAvailablePrograms = () => {
    if (!selectedAreaOfStudy) return [];
    return uagcPrograms.filter(prog => {
      const areaMatch = prog.areaOfStudy === selectedAreaOfStudy;
      const degreeMatch = !selectedDegreeLevel || prog.degreeLevel === selectedDegreeLevel;
      return areaMatch && degreeMatch;
    });
  };
  const handleDegreeLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    setSelectedDegreeLevel(newLevel);
    setSelectedAreaOfStudy('');
    setSelectedProgram('');
  };
  const handleAreaOfStudyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newArea = e.target.value;
    setSelectedAreaOfStudy(newArea);
    setSelectedProgram('');
  };
  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProgram = e.target.value;
    setSelectedProgram(newProgram);
    if (newProgram && formStep === 0) {
      setFormStep(1);
    }
  };
  const currentProgram = uagcPrograms.find(p => p.mProgram === selectedProgram);
  const handleStep1Next = () => {
    if (validateUniStep1()) setFormStep(2);
  };
  const handleStep2Next = () => {
    const zipValid = validateUniStep2();
    const addrValid = formData.address.trim().length > 0;
    if (!addrValid) {
      setAddressError('Street address is required.');
    } else {
      setAddressError('');
    }
    if (zipValid && addrValid) {
      lookupZip(formData.zipCode);
      setFormStep(3);
    }
  };
  const handleStep3Submit = async () => {
    if (validateUniStep3()) {
      setIsSubmitting(true);
      try {
        const params = new URLSearchParams({
          ...urlParams,
          mProgram: currentProgram?.mProgram || '',
          programTitle: currentProgram?.longTitle || '',
          degreeLevel: selectedDegreeLevel,
          areaOfStudy: selectedAreaOfStudy,
          highestEducation: formData.currentEducation,
          gradYear: formData.gradYear,
          startTimeframe: formData.startDate,
          zipCode: formData.zipCode,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: stripPhone(formData.phone),
          leadIdToken: leadIdToken,
          trustedFormUrl: trustedFormUrl,
          webLandingURL: window.location.href,
          userIP: userIP
        });
        await fetch(`https://hooks.zapier.com/hooks/catch/6973915/uv7w6tl/?${params.toString()}`, {
          method: 'GET',
          mode: 'no-cors'
        });
      } catch (err) {
        console.error('Webhook submission error:', err);
      } finally {
        setIsSubmitting(false);
      }
      navigate('/thank-you', {
        state: {
          formData,
          partner: {
            name: 'The University of Arizona Global Campus',
            logo: 'https://cdn.magicpatterns.com/uploads/9YZ3itxkAEq4QwKPaBxWP6/UAGC_LOGO.webp',
            primaryColor: '#1C2B39',
            accentColor: '#CC0033',
            backUrl: '/colleges/university-of-arizona-global-campus',
            programTitle: currentProgram?.longTitle || ''
          }
        }
      });
    }
  };
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const totalSteps = 3;
  const progressPercent = formStep === 0 ? 0 : Math.round(formStep / totalSteps * 100);
  const selectClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md bg-white appearance-none cursor-pointer ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const inputClasses = (hasError: boolean) => `block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${hasError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`;
  const availableAreas = getAvailableAreas();
  const availablePrograms = getAvailablePrograms();
  const renderFormContent = () => {
    if (formStep === 0) {
      return <div className="space-y-4" data-id="element-3131" data-mp="227e3a0n8glr">
          <div data-id="element-3132" data-mp="3u833y84rc0ok">
            <label htmlFor="degree-level-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3133" data-mp="3u7jlmqb73aa0">
              Degree Level
            </label>
            <div className="relative" data-id="element-3134" data-mp="3kbzs2ak3snof">
              <select id="degree-level-select" value={selectedDegreeLevel} onChange={handleDegreeLevelChange} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800" data-id="element-3135" data-mp="ls9olwm5wv93">
                <option value="" data-id="element-3136" data-mp="71sn6d9j25c7">-- Select Degree Level --</option>
                {degreeLevels.map(level => <option key={level.value} value={level.value} data-id="element-3137" data-mp="zsalz237xkjt">
                    {level.label}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-3138" data-mp="2u0xz1dif4g4b">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3139" data-mp="2y3kjuugo90s0" />
              </div>
            </div>
          </div>

          <div data-id="element-3140" data-mp="2p9g0sx7s4tn1">
            <label htmlFor="area-of-study-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3141" data-mp="2p8wgxaykjv6d">
              Area of Study
            </label>
            <div className="relative" data-id="element-3142" data-mp="2fcbaqmatce0y">
              <select id="area-of-study-select" value={selectedAreaOfStudy} onChange={handleAreaOfStudyChange} disabled={!selectedDegreeLevel} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-3143" data-mp="2f95kh3qg763n">
                <option value="" data-id="element-3144" data-mp="el1cpybxfn2f">
                  {selectedDegreeLevel ? '-- Select Area of Study --' : 'Select degree level first'}
                </option>
                {availableAreas.map(slug => <option key={slug} value={slug} data-id="element-3145" data-mp="ioh3wf588v7q">
                    {areaLabels[slug]}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-3146" data-mp="xfgaxlqezbp7">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3147" data-mp="16e1qd80laalr" />
              </div>
            </div>
          </div>

          <div data-id="element-3148" data-mp="12czr4r6o9yjs">
            <label htmlFor="program-select" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3149" data-mp="12cgi5zz02khw">
              Program
            </label>
            <div className="relative" data-id="element-3150" data-mp="3454nqfz49k0u">
              <select id="program-select" value={selectedProgram} onChange={handleProgramChange} disabled={!selectedAreaOfStudy} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md bg-gray-50 appearance-none cursor-pointer focus:ring-[#CC0033] focus:border-[#CC0033] font-medium text-gray-800 disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400" data-id="element-3151" data-mp="346632wn60sod">
                <option value="" data-id="element-3152" data-mp="39rzz1p30l2vk">
                  {!selectedAreaOfStudy ? 'Select area of study first' : '-- Select Program --'}
                </option>
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-3153" data-mp="3nqg7plkft3gc">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500" data-id="element-3154" data-mp="12zph11uw8r4v">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3155" data-mp="1d3cuf6gg64wc" />
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center pt-2" data-id="element-3156" data-mp="1841fm2zn59he">
            Select all three options above to continue
          </p>
        </div>;
    }
    if (formStep === 1) {
      return <div className="space-y-4" data-id="element-3157" data-mp="zcr9lc7fnkvt">
          <div data-id="element-3158" data-mp="zbpyj7qf2ye3">
            <label htmlFor="step1-program" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3159" data-mp="za54dj80lhgr">
              Selected Program
            </label>
            <div className="relative" data-id="element-3160" data-mp="keyz4ygqtmoh">
              <select id="step1-program" value={selectedProgram} onChange={e => {
              setSelectedProgram(e.target.value);
            }} className="block w-full px-4 py-3 text-sm border border-gray-200 rounded-md bg-gray-50 appearance-none cursor-pointer font-medium text-gray-700" data-id="element-3161" data-mp="kg0al41o8b6q">
                {availablePrograms.map(prog => <option key={prog.mProgram} value={prog.mProgram} data-id="element-3162" data-mp="hceleqa5nn71">
                    {prog.shortTitle}
                  </option>)}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-400" data-id="element-3163" data-mp="8jkyc6ogvjim">
                <ChevronDownIcon className="h-4 w-4" data-id="element-3164" data-mp="3lj5wv55i7uh" />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-3" data-id="element-3165" data-mp="3upzpjmy3lymh" />

          <div data-id="element-3166" data-mp="3pry7n5ces5cl">
            <label htmlFor="currentEducation" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3167" data-mp="3ps7vam9ze0f4">
              Highest Level of Education
            </label>
            <select id="currentEducation" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClasses(!!errors.currentEducation)} data-id="element-3168" data-mp="3gue1pj3lpkvn">
              <option value="" data-id="element-3169" data-mp="1mims8wh6gxv1">-- Please Select --</option>
              {uagcEducationLevels.map(level => <option key={level.value} value={level.value} data-id="element-3170" data-mp="1waabwb7htaly">
                  {level.label}
                </option>)}
            </select>
            {errors.currentEducation && <p className="mt-1 text-xs text-red-600" data-id="element-3171" data-mp="13llasc6ft5a7">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-3172" data-mp="18lhe097b8sop">
            <label htmlFor="startDate" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3173" data-mp="18l7v15konlsi">
              When Would You Like to Start?
            </label>
            <select id="startDate" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClasses(!!errors.startDate)} data-id="element-3174" data-mp="1hj1idr0gz53j">
              <option value="" data-id="element-3175" data-mp="tv02vaia926p">-- Please Select --</option>
              {uagcStartTimeframes.map(opt => <option key={opt.value} value={opt.value} data-id="element-3176" data-mp="ovn8m4p30su3">
                  {opt.label}
                </option>)}
            </select>
            {errors.startDate && <p className="mt-1 text-xs text-red-600" data-id="element-3177" data-mp="xx4uy4t31qr8">{errors.startDate}</p>}
          </div>

          <div data-id="element-3178" data-mp="a8tydduf6d8r">
            <label htmlFor="gradYear" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3179" data-mp="a8btxtxlsspr">
              High School Graduation Year
            </label>
            <select id="gradYear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClasses(!!errors.gradYear)} data-id="element-3180" data-mp="jzoo4s6vjuyy">
              <option value="" data-id="element-3181" data-mp="3io5b8azre87v">-- Please Select --</option>
              {gradYearOptions.map(yr => <option key={yr} value={yr} data-id="element-3182" data-mp="1y5ba51om5ts5">
                  {yr}
                </option>)}
            </select>
            {errors.gradYear && <p className="mt-1 text-xs text-red-600" data-id="element-3183" data-mp="22y4h7w7k1e7e">{errors.gradYear}</p>}
          </div>

          <div className="flex gap-3 pt-2" data-id="element-3184" data-mp="1u3zhsoecuug9">
            <button type="button" onClick={() => setFormStep(0)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3185" data-mp="1u5127jh9gtan">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3186" data-mp="27ubaopirkx8g" />
              Back
            </button>
            <button type="button" onClick={handleStep1Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3187" data-mp="2cto5pxjsham4">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 2) {
      return <div className="space-y-4" data-id="element-3188" data-mp="1bwo1g12yma7r">
          <div data-id="element-3189" data-mp="17wna5furi4ja">
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3190" data-mp="17w42qt2qmyji">
              Zip Code
            </label>
            <input type="text" id="zipCode" value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} className={inputClasses(!!errors.zipCode)} placeholder="12345" maxLength={5} data-id="element-3191" data-mp="1zobi7q6ic4wb" />
            {errors.zipCode && <p className="mt-1 text-xs text-red-600" data-id="element-3192" data-mp="a7aiew9e3kdu">{errors.zipCode}</p>}
            {zipLoading && <div className="mt-2 flex items-center text-xs text-gray-500" data-id="element-3193" data-mp="2u3q67nc9bm">
                <LoaderIcon className="w-3 h-3 mr-1 animate-spin" data-id="element-3194" data-mp="54jctgbb6cx2" />
                Looking up your location...
              </div>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-3195" data-mp="ev5nqx82yf9f">
                <div data-id="element-3196" data-mp="iw7ppltdt5f3">
                  <label htmlFor="city" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3197" data-mp="iumwbziszsit">
                    City
                  </label>
                  <input type="text" id="city" value={formData.city} onChange={e => updateFormData('city', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="City" data-id="element-3198" data-mp="srj3o1wyu8ls" />
                </div>
                <div data-id="element-3199" data-mp="2ml2q8gtl2xfo">
                  <label htmlFor="state" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3200" data-mp="2mlkqrpna3xte">
                    State
                  </label>
                  <input type="text" id="state" value={formData.state} onChange={e => updateFormData('state', e.target.value)} className="block w-full px-4 py-3 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-[#CC0033] focus:border-[#CC0033] bg-gray-50" placeholder="State" data-id="element-3201" data-mp="2wktl4jm23ega" />
                </div>
              </div>
              <div data-id="element-3202" data-mp="3t9994b9ipfra">
                <label htmlFor="address" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3203" data-mp="3t6n68oxknofe">
                  Street Address
                </label>
                <input type="text" id="address" value={formData.address} onChange={e => {
              updateFormData('address', e.target.value);
              if (addressError) setAddressError('');
            }} className={`block w-full px-4 py-3 text-sm border rounded-md shadow-sm ${addressError ? 'border-red-400' : 'border-gray-300 focus:ring-[#CC0033] focus:border-[#CC0033]'}`} placeholder="123 Main St" data-id="element-3204" data-mp="gpne6zt9lqfb" />
                {addressError && <p className="mt-1 text-xs text-red-600" data-id="element-3205" data-mp="3k5kp5az932ep">{addressError}</p>}
              </div>
            </>}

          <div className="flex gap-3 pt-2" data-id="element-3206" data-mp="367dxwdot40o0">
            <button type="button" onClick={() => setFormStep(1)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3207" data-mp="3b562o8ajvh9m">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3208" data-mp="3lm9mj1dmnase" />
              Back
            </button>
            <button type="button" onClick={handleStep2Next} className="flex-1 bg-gradient-to-r from-[#1C2B39] to-[#003366] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200" data-id="element-3209" data-mp="3hobwxxfy2moh">
              Next
            </button>
          </div>
        </div>;
    }
    if (formStep === 3) {
      return <div className="space-y-4" data-id="element-3210" data-mp="2tx6edaimdgah">
          <div className="grid grid-cols-2 gap-3" data-id="element-3211" data-mp="2ty7qzjfaaaub">
            <div data-id="element-3212" data-mp="2oxtsarzkod8y">
              <label htmlFor="firstName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3213" data-mp="2oyd55uad8bg4">
                First Name
              </label>
              <input type="text" id="firstName" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} className={inputClasses(!!errors.firstName)} placeholder="Jane" data-id="element-3214" data-mp="zfmzajip81js" />
              {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-3215" data-mp="gq6uvl3w7oy3">{errors.firstName}</p>}
            </div>
            <div data-id="element-3216" data-mp="lp1n1dnr2puv">
              <label htmlFor="lastName" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3217" data-mp="loi5hy20i3hd">
                Last Name
              </label>
              <input type="text" id="lastName" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} className={inputClasses(!!errors.lastName)} placeholder="Doe" data-id="element-3218" data-mp="uieoj3zvqzsk" />
              {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-3219" data-mp="38sfy08toycu2">{errors.lastName}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-3220" data-mp="2ywmc3yu316zm">
            <div data-id="element-3221" data-mp="2yvus7l9ukxp6">
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3222" data-mp="2yygub5dyyl00">
                Email Address
              </label>
              <input type="email" id="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} className={inputClasses(!!errors.email)} placeholder="jane@example.com" data-id="element-3223" data-mp="2twrllwe7yjf3" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-3224" data-mp="267ywojw0lbtz">{errors.email}</p>}
            </div>
            <div data-id="element-3225" data-mp="2b60b2rihwmz4">
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wider" data-id="element-3226" data-mp="2b8mo36o5nuoq">
                Phone Number
              </label>
              <input type="tel" id="phone" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} className={inputClasses(!!errors.phone)} placeholder="(555)123-4567" maxLength={13} data-id="element-3227" data-mp="21iwh5ixx5gef" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-3228" data-mp="2k9xc7vvx4rsp">{errors.phone}</p>}
            </div>
          </div>

          <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#CC0033] focus:ring-[#CC0033] cursor-pointer flex-shrink-0" data-id="element-3229" data-mp="2fbm35ndg6lbj">
            <>
                By selecting Request Information, I consent to receive calls,
                emails, and texts from the University of Arizona Global Campus
                (UAGC) which may be sent using automated or prerecorded
                technology. Message and data rates may apply; frequency may
                vary. This consent is not a condition of applying, enrolling, or
                purchase. I may contact UAGC directly at 866-711-1700, I may opt
                out of SMS anytime by replying STOP, and can text HELP for
                assistance.{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/uwnAfhD-utqnhD___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6NWFlZDplNzRhOWMyMmMxZmRmOTJlOGI0ZmI1NTU3NjBmMWQxMDRlN2M5NzVmNmRhN2M2NDY5YWFlNmZhNWZjYzc1OGZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-3230" data-mp="3p15jd6mtwogg">
                  Privacy Policy
                </a>
                ,{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/yjwrx-fsi-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6ODA3ZTowMTFlY2JiNTVhZjhmMjE3ODhjMGMwYThmZDczMmVhMTk2MGI3ZmUzZGNmYWQ2NGM1M2FlMDY3ZTBmNjAxM2RiOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-3231" data-mp="1ibkfw2yz9553">
                  Terms and Conditions
                </a>{' '}
                of Usage, and{' '}
                <a href="https://protect.checkpoint.com/v2/r01/___https://www.uagc.edu/xrx-yjwrx-htsinyntsx___.YzJ1OnpldGFnbG9iYWxoZWM6YzpvOmExMjJmYzVlMjFjNDM0NDI2MzM3ZmVmMWM5YTQ0MzAyOjc6OWIyNDpmZmQyOTUwMDZmNzIwZTRkMjdkY2NmOWU1NjZhMWM3OGM3N2QyNGE2YmExYmViZmZkYWU1MjQ5NTJjNjA5MjZmOmg6VDpU" target="_blank" rel="noopener noreferrer" className="underline text-gray-700 hover:text-[#1C2B39]" data-id="element-3232" data-mp="pnev5g7xbg59">
                  SMS Terms and Conditions
                </a>{' '}
                apply.
            </>
          </TCPADisclosure>

          <div className="flex gap-3 pt-2" data-id="element-3233" data-mp="2s9a5283j4bie">
            <button type="button" onClick={() => setFormStep(2)} className="flex items-center justify-center px-5 py-3.5 border border-gray-300 text-sm font-bold rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-3234" data-mp="2sabph36fqacs">
              <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-3235" data-mp="2n8mfzryv24qt" />
              Back
            </button>
            <button type="button" onClick={handleStep3Submit} disabled={isSubmitting} className="flex-1 bg-gradient-to-r from-[#CC0033] to-[#A50028] hover:shadow-lg text-white font-bold py-3.5 px-6 rounded-full text-sm tracking-wider uppercase transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-3236" data-mp="16umlwxmp59bh">
              {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-3237" data-mp="2vheml2ipnrw" />}
              {isSubmitting ? 'Submitting...' : 'Request Info'}
            </button>
          </div>
        </div>;
    }
    return null;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-3238" data-mp="32d7eagwt6qnh">
      {/* Header */}
      <header className="bg-white py-2 sm:py-3 px-4 sm:px-12 flex items-center justify-center shadow-sm" data-id="element-3239" data-mp="3g4c5qvq0wcmq">
        <img src="https://cdn.magicpatterns.com/uploads/hZrXUkDbsWP7copst5KXLY/UAGC.png" alt="University of Arizona Global Campus Logo" className="h-10 sm:h-16 w-auto object-contain" data-id="element-3240" data-mp="3bdvrxaiaiozu" />
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2A3F52] to-[#1A4D80] relative overflow-hidden" data-id="element-3241" data-mp="2jm7k91agiqnc">
        {/* Decorative image overlay */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none bg-no-repeat bg-cover bg-center mix-blend-soft-light opacity-70" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8uFwSQxxc9xYA37moQruEX/ChatGPT_Image_May_6,_2026,_08_20_39_PM.png')"
      }} data-id="element-3242" data-mp="rkejzvlvd3nm" />
        {/* Explicit grid placement rather than duplicated copy: on mobile the
            form follows the headline so it lands within reach, and the
            supporting paragraph moves below it. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-24 grid gap-8 lg:grid-cols-2 lg:gap-x-16 lg:gap-y-6 relative z-10" data-id="element-3243" data-mp="dnhvw1x4y0af">
          {/* Headline */}
          <div className="text-white max-w-xl mx-auto lg:mx-0 text-center lg:text-left lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-3244" data-mp="1ozsexgm2ui1o">
            <p className="text-white font-medium mb-3 text-lg tracking-wide" data-id="element-3245" data-mp="1u183hingr6ot">
              University of Arizona Global Campus
            </p>
            <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-white tracking-tight" data-id="element-3246" data-mp="39je1h7x3vdty">
              {currentProgram ? currentProgram.longTitle : <>
                  Flexible Online
                  <br data-id="element-3247" data-mp="2ut6tjm4q5d7i" />
                  Degree Programs
                </>}
            </h1>
          </div>

          {/* Right Form */}
          <div ref={formRef} className="w-full lg:w-[520px] lg:justify-self-end bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] p-6 sm:p-10 relative z-10 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:self-center" data-id="element-3248" data-mp="2qqa6nj85iuxr">
            <form onSubmit={e => e.preventDefault()} id="uagc-lead-form" data-id="element-3249" data-mp="k1x67d6yn2ou">
              {/* Hidden inputs for tracking */}
              <input type="hidden" name="xxTrustedFormCertUrl" id="xxTrustedFormCertUrl" defaultValue="" data-id="element-3250" data-mp="f3lvz1cr6q63" />
              <input type="hidden" name="leadid_token" id="leadid_token" defaultValue="" data-id="element-3251" data-mp="3p0dyzzvebtbt" />

              <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-3252" data-mp="3fbckyf8evm0h">
                Take the next step in your education.
              </h2>

              {/* Progress Bar */}
              <div className="w-full bg-gray-100 rounded-full h-2 mb-8 overflow-hidden" data-id="element-3253" data-mp="36axw55w8nos8">
                <div className="bg-gradient-to-r from-[#CC0033] to-[#A50028] h-2 rounded-full transition-all duration-500 ease-out" style={{
                width: `${progressPercent}%`
              }} data-id="element-3254" data-mp="368l768s2ce5o"></div>
              </div>

              <AnimatePresence mode="wait" data-id="element-3255" data-mp="pk7iq3i25hm8">
                <motion.div key={formStep} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-3256" data-mp="uln6427pk07s">
                  {renderFormContent()}
                </motion.div>
              </AnimatePresence>
            </form>
          </div>

          {/* Supporting copy. After the form in the markup so mobile gets it
              below; the grid puts it back under the headline on desktop. */}
          <p className="text-base lg:text-lg text-white/85 max-w-lg mx-auto lg:mx-0 text-center lg:text-left leading-relaxed lg:col-start-1 lg:row-start-2 lg:self-start" data-id="element-3257" data-mp="32t9xarcsnsa7">
            The University of Arizona Global Campus offers accredited online
            degree programs designed for working adults. With flexible
            scheduling, dedicated support, and a connection to the University of
            Arizona, we help you achieve your educational and career goals.
          </p>
        </div>
      </section>

      {/* Reasons to Choose UAGC */}
      <section className="bg-white py-16 border-b border-gray-200" data-id="element-3258" data-mp="2jet3h5q8izmp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3259" data-mp="2eddhnbg8gp39">
          <div className="mb-10" data-id="element-3260" data-mp="2se6bdtypng5b">
            <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-3261" data-mp="2sd4rr13mplbz"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39]" data-id="element-3262" data-mp="2ocktfu7bkbo9">
              Reasons to Choose UAGC
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3263" data-mp="322oam6rcdf6u">
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-3264" data-mp="2x4edvrpyy6j4">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-3265" data-mp="3c26tejvimuxb">
                <ClipboardCheckIcon className="w-7 h-7 text-[#CC0033]" data-id="element-3266" data-mp="373ca7gtmz4co" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-3267" data-mp="3qovzj4k9w6co">
                No SAT, GMAT, GRE, or ACT Required
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3268" data-mp="3upfz0esbjm1z">
                Standardized test scores are not required for enrollment.
              </p>
            </div>
            <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-8" data-id="element-3269" data-mp="3lp9n2xc9qu4x">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-3270" data-mp="370wmto65ep4m">
                <ClockIcon className="w-7 h-7 text-[#CC0033]" data-id="element-3271" data-mp="3brd5pnsqn2yd" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-3272" data-mp="2y06xn3be7wzv">
                5- to 6-week Classes*
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3273" data-mp="32yrviw36a4l5">
                Take one class at a time as a full-time student.
              </p>
            </div>
            <div data-id="element-3274" data-mp="2o4ddoyvch182">
              <div className="w-14 h-14 bg-[#CC0033]/10 rounded-lg flex items-center justify-center mb-4" data-id="element-3275" data-mp="2t0ub6joforxt">
                <DollarSignIcon className="w-7 h-7 text-[#CC0033]" data-id="element-3276" data-mp="29x0nt36e4wbx" />
              </div>
              <h3 className="text-lg font-bold text-[#1C2B39] mb-2" data-id="element-3277" data-mp="256219lut7208">
                $0 Application Fee
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3278" data-mp="205w45q5rsnar">
                Apply to UAGC risk-free.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose UAGC */}
      <section className="py-16 bg-gray-50" data-id="element-3279" data-mp="2su1pae0qkeb4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3280" data-mp="2nwa0hsve18b5">
          <div className="flex flex-col lg:flex-row items-start gap-12" data-id="element-3281" data-mp="2nx1l68ng5lmn">
            <div className="flex-1" data-id="element-3282" data-mp="2iw5l6667ujmi">
              <div className="w-12 h-1 bg-[#CC0033] mb-4" data-id="element-3283" data-mp="2iz9ufhyjg5k1"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-8" data-id="element-3284" data-mp="2dxapw4r2b2li">
                Why Choose <span className="text-[#CC0033]" data-id="element-3285" data-mp="17j30ayx337wj">UAGC</span>
              </h2>
              <ul className="space-y-4" data-id="element-3286" data-mp="12ifcem45j76j">
                <li className="flex items-start" data-id="element-3287" data-mp="12hdsds55raco">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3288" data-mp="3e6slq21507uy" />
                  <span className="text-gray-700" data-id="element-3289" data-mp="3j4u40kqqo35d">
                    <strong data-id="element-3290" data-mp="3j5n3d0ndbqdh">Transfer approved college credits</strong> towards
                    your bachelor's program*
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3291" data-mp="3sw89ra9a9z6y">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3292" data-mp="3swzvltd2wik1" />
                  <span className="text-gray-700" data-id="element-3293" data-mp="1r85gtzax5x8">
                    <strong data-id="element-3294" data-mp="5pfq3zwvyrdp">
                      College credit for relevant work/life experience
                    </strong>{' '}
                    through Prior Learning Assessment & Experiential Learning
                    Credit Programs
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3295" data-mp="2chq48c1vyiz4">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3296" data-mp="2cj9oedqnxe66" />
                  <span className="text-gray-700" data-id="element-3297" data-mp="2hhuqkiph67x9">
                    <strong data-id="element-3298" data-mp="bqodbrfq8mxj">Attend for 3 weeks risk-free</strong> to see if UAGC
                    is right for you
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3299" data-mp="ksfdyhveu3kj">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3300" data-mp="kr49sgy1ytsw" />
                  <div className="text-gray-700" data-id="element-3301" data-mp="gs58d6bj5f67">
                    <strong data-id="element-3302" data-mp="3p6dxg4eufntm">
                      One course at a time in an accelerated format
                    </strong>
                    <ul className="mt-2 ml-4 space-y-1 text-sm text-gray-600" data-id="element-3303" data-mp="3k8c4mrrm8a4y">
                      <li data-id="element-3304" data-mp="1tbcbu677c2w">• 5 weeks for undergraduate degrees</li>
                      <li data-id="element-3305" data-mp="1vo22ti76qqi">• 6 weeks for graduate programs</li>
                      <li data-id="element-3306" data-mp="3t764cr08tgmq">• 9 weeks for doctoral dissertation courses</li>
                    </ul>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-3307" data-mp="35ga7hfgxjv8m">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3308" data-mp="31g03g8cp7ewe" />
                  <span className="text-gray-700" data-id="element-3309" data-mp="2rc36axwp0q7o">
                    <strong data-id="element-3310" data-mp="2rb9uhiivnaio">Access the online classroom 24/7</strong> so you can
                    study at night, on the weekends, or during lunch breaks
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3311" data-mp="2hd3whc06yoj3">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3312" data-mp="2mf2xif8gl9cv" />
                  <span className="text-gray-700" data-id="element-3313" data-mp="3a4nhn1gcmcnq">
                    <strong data-id="element-3314" data-mp="3f2p2nrxc4m1u">Learn from expert faculty</strong> with real-world
                    experience in their respective fields
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3315" data-mp="307iwn4y8on8i">
                  <CheckCircle2Icon className="w-5 h-5 text-[#CC0033] mr-3 flex-shrink-0 mt-0.5" data-id="element-3316" data-mp="353q2kxqmxyqg" />
                  <span className="text-gray-700" data-id="element-3317" data-mp="1opzwxmjvb2ep">
                    <strong data-id="element-3318" data-mp="1osdsrxmdginb">Join the Honors Program</strong> for high-achieving
                    bachelor's students
                  </span>
                </li>
              </ul>
            </div>
            <div className="lg:w-[400px] flex-shrink-0" data-id="element-3319" data-mp="8pzvjuny59f">
              <img src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=500&fit=crop&q=80" alt="Student studying online" className="w-full h-[400px] object-cover rounded-2xl shadow-lg" data-id="element-3320" data-mp="3rdzmr2nd23et" />
            </div>
          </div>
        </div>
      </section>

      {/* Student Testimonials */}
      <section className="bg-[#1C2B39] py-16" data-id="element-3321" data-mp="38moxepxa9yp1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3322" data-mp="38kvof3z772dv">
          <div className="flex flex-col lg:flex-row gap-0 rounded-2xl overflow-hidden" data-id="element-3323" data-mp="33mu8ut0zdl75">
            {/* Left intro */}
            <div className="bg-[#1C2B39] p-8 lg:p-12 flex flex-col justify-center lg:w-1/4" data-id="element-3324" data-mp="2ypapxftc8k8v">
              <p className="text-xs font-bold text-white/60 uppercase tracking-widest mb-3" data-id="element-3325" data-mp="11uxxbjetcbcd">
                Student Experience
              </p>
              <h2 className="text-3xl font-bold text-white leading-tight" data-id="element-3326" data-mp="wv34habh9boo">
                Praise for UAGC
              </h2>
              <p className="text-white/70 text-sm mt-3" data-id="element-3327" data-mp="19f8e9754meee">
                In their own words, why students love UAGC
              </p>
            </div>
            {/* Testimonial cards */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-0" data-id="element-3328" data-mp="1j5bgzz21v6px">
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-3329" data-mp="1j7e5ufrgutt2">
                <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=500&fit=crop&q=80" alt="Vicente Rocco Vargas" className="w-full h-full object-cover" data-id="element-3330" data-mp="liul8y8yt16a" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-3331" data-mp="10ek3clb14u5q"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3332" data-mp="26vfdznkqg87y">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-3333" data-mp="2btyxzifrn9xo">
                    "I've tried to send so many people to UAGC because of how
                    well they've treated me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-3334" data-mp="2rzgbqmu2n5ut">
                    Vicente "Rocco" Vargas
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-3335" data-mp="31x49llmz4th7">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=500&fit=crop&q=80" alt="Jessica Smith" className="w-full h-full object-cover" data-id="element-3336" data-mp="31vt6zp59lrro" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-3337" data-mp="3pn8dlu42qv8q"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3338" data-mp="3knc4xhkfmfmu">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-3339" data-mp="1v8fhzv36y4gs">
                    "Life has totally changed because there were so many things
                    that came out of this experience for me."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-3340" data-mp="5sv5ibr6rilz">
                    Jessica Smith
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-auto overflow-hidden" data-id="element-3341" data-mp="3s7o7jpdqgsuz">
                <img src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop&q=80" alt="Wendy Bosquez" className="w-full h-full object-cover" data-id="element-3342" data-mp="3s5ac3ff55emw" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-3343" data-mp="3o6krb1rrwut8"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-3344" data-mp="399k7rljqk65u">
                  <p className="text-white text-sm italic leading-relaxed mb-3" data-id="element-3345" data-mp="3e6k61aacg4me">
                    "I believe that my future, my destiny is now bigger than my
                    dreams ever since being a part of UAGC."
                  </p>
                  <p className="text-white/80 text-xs font-medium" data-id="element-3346" data-mp="1cf7cfhdttj5n">
                    Wendy Bosquez
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditation Section */}
      <section className="py-16 bg-white" data-id="element-3347" data-mp="2mp2ifauo1cey">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3348" data-mp="2mn7uros0f676">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-3349" data-mp="2rhlta01yxtg2"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1C2B39] mb-6" data-id="element-3350" data-mp="2wfx3ibw6e5yt">
            Accreditation = Quality Education
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4" data-id="element-3351" data-mp="31cdqv3vjwgau">
            UAGC maintains institutional accreditation and several programmatic
            accreditations, which means that it consistently meets quality
            standards that all students deserve.
          </p>
          <p className="text-gray-600 leading-relaxed" data-id="element-3352" data-mp="1b32oa8ykn9w7">
            So, whether you are <strong data-id="element-3353" data-mp="1g02j1npz0q80">transferring credits†</strong> you have
            earned from another institution, or you are just{' '}
            <strong data-id="element-3354" data-mp="18i4ocpcg7jou">starting your college journey</strong>, you can rest assured
            knowing you are receiving the most for your investment at UAGC.
          </p>
        </div>
      </section>

      {/* Try Your First Course Free */}
      <section className="py-16 bg-gray-50 border-t border-gray-200" data-id="element-3355" data-mp="yp6zm1gv4yhs">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3356" data-mp="tu1ev5z75pva">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1C2B39] mb-4" data-id="element-3357" data-mp="1rgh1em6hddpp">
            Try Your First Course Complementary for 3 Weeks: No Cost, No Risk,
            Total Confidence
          </h2>
          <p className="text-gray-600 leading-relaxed mb-10" data-id="element-3358" data-mp="1hh9mhuk3lb0h">
            The UAGC Promise affords new degree-seeking students the opportunity
            to get a feel for the UAGC online platform before enrolling to make
            sure it's the right fit. With the UAGC Promise, attend for the first
            three weeks of your first course without any financial risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-3359" data-mp="3hwmdcm4i7jn">
            <div className="flex flex-col items-center" data-id="element-3360" data-mp="8hj521kq0fzu">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-3361" data-mp="8h108gjzstgb">
                <ShieldCheckIcon className="w-8 h-8 text-[#CC0033]" data-id="element-3362" data-mp="3ppedfqb21zfk" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-3363" data-mp="3un6aeyqk4bqm">
                Attend for 3 Weeks Risk Complementary
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-3364" data-mp="3lokvddkaneul">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-3365" data-mp="3lq5p50ysatre">
                <DollarSignIcon className="w-8 h-8 text-[#CC0033]" data-id="element-3366" data-mp="36xbwseyeyj5b" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-3367" data-mp="3bw5c8cx9gk84">
                Start School with No Financial Burden
              </h3>
            </div>
            <div className="flex flex-col items-center" data-id="element-3368" data-mp="31z0ldzsh00kz">
              <div className="w-16 h-16 bg-[#CC0033]/10 rounded-full flex items-center justify-center mb-4" data-id="element-3369" data-mp="2jbrrv38xyzwo">
                <LaptopIcon className="w-8 h-8 text-[#CC0033]" data-id="element-3370" data-mp="2ecycf5a3gytv" />
              </div>
              <h3 className="text-base font-bold text-[#1C2B39]" data-id="element-3371" data-mp="2ef1dqlgp6edw">
                Flexibility to Find Your Fit
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1C2B39] py-16" data-id="element-3372" data-mp="32adw2qzr974t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3373" data-mp="3292sar6b7zdp">
          <div className="w-12 h-1 bg-[#CC0033] mx-auto mb-6" data-id="element-3374" data-mp="2xahqinbet7n5"></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-id="element-3375" data-mp="3b2pk667ecihm">
            Interested in Learning More About Our{' '}
            <span className="text-[#D4A843]" data-id="element-3376" data-mp="372f1pu2f3zlv">Degrees?</span>
          </h2>
          <div className="mt-8" data-id="element-3377" data-mp="3l0vh09eciyfm">
            <button onClick={scrollToForm} className="inline-block bg-white text-[#1C2B39] font-bold py-3.5 px-12 rounded-full hover:shadow-lg hover:bg-gray-100 transition-all text-sm tracking-wide" data-id="element-3378" data-mp="3l1wvknukm323">
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F5F5] text-gray-500 py-10 text-xs border-t border-gray-200" data-id="element-3379" data-mp="crcgscz5ua0o">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3" data-id="element-3380" data-mp="hq5tw4ajbz0b">
          <p data-id="element-3381" data-mp="hs9wmiwp6rz0">Certain degree programs may not be available in all states.</p>
          <p data-id="element-3382" data-mp="3h6vgsl82pcom">
            * Classes last 5 weeks for undergraduate programs, 6 weeks for
            graduate programs, and 9 weeks for doctoral capstone, planning and
            project classes.
          </p>
          <p data-id="element-3383" data-mp="3q4nwhqhpimcj">
            † The transferability of credits is subject to the University of
            Arizona Global Campus transfer credit policies and requires the
            submission of official transcripts. The official transcripts will be
            evaluated to determine the credits that will officially apply toward
            a UAGC degree program.
          </p>
          <p className="pt-4 border-t border-gray-300 text-gray-400" data-id="element-3384" data-mp="2uvkc7l8fz95v">
            The University of Arizona Global Campus is accredited by the Higher
            Learning Commission (hlcommission.org), an institutional
            accreditation agency recognized by the U.S. Department of Education.
          </p>
          <LegalLinks className="pt-2" data-id="element-3385" data-mp="2g7g0x2bpxzx4" />
          <p className="text-gray-400" data-id="element-3386" data-mp="2c6dykcmi97qx">
            © {new Date().getFullYear()} University of Arizona Global Campus.
            All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}